import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { usePermissions } from "../hooks/usePermissions";
import {
    ShoppingCart, Plus, AlertTriangle, CheckCircle2, XCircle, ArrowRight, Loader2, X,
} from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = ["Pending", "Allocated", "Shipped", "Delivered", "Override Pending", "Cancelled"];
const STATUS_CHIP = {
    "Pending": "border-snow-muted text-snow-muted",
    "Allocated": "border-signal text-signal",
    "Shipped": "border-snow text-snow",
    "Delivered": "border-green-500/40 text-green-400",
    "Override Pending": "border-signal bg-signal/10 text-signal animate-pulse-signal",
    "Cancelled": "border-red-500/40 text-red-400",
};
const NEXT_STATE = { Pending: "Allocated", Allocated: "Shipped", Shipped: "Delivered" };

/**
 * Build Prompt #14 — the dedicated /orders page is the landing surface for Demand roles.
 * Demand Lead can manage orders + approve overrides; Demand Operator can manage orders;
 * Logistics roles arrive here from the sidebar to view + transition orders.
 */
export default function Orders() {
    const perm = usePermissions();
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [overrideQueue, setOverrideQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("active"); // active | overrides | all
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ customer_name: "", sku: "", quantity: 1, fulfillment_type: "Internal" });
    const [busy, setBusy] = useState(false);
    const [modalErr, setModalErr] = useState("");
    const [overrideTarget, setOverrideTarget] = useState(null); // { order, action }
    const [overrideReason, setOverrideReason] = useState("");

    const refresh = async () => {
        try {
            const [ord, p, q] = await Promise.all([
                api.get("/orders").then((r) => r.data),
                perm.viewProducts || perm.viewProductsLimited
                    ? api.get("/products").then((r) => r.data)
                    : Promise.resolve([]),
                perm.approveOrderOverride
                    ? api.get("/dashboard/order-override-queue").then((r) => r.data)
                    : Promise.resolve([]),
            ]);
            setOrders(ord || []);
            setProducts(p || []);
            setOverrideQueue(q || []);
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const stats = useMemo(() => ({
        total: orders.length,
        pending: orders.filter((o) => o.order_status === "Pending").length,
        allocated: orders.filter((o) => o.order_status === "Allocated").length,
        shipped: orders.filter((o) => o.order_status === "Shipped").length,
        overrides: orders.filter((o) => o.order_status === "Override Pending").length,
    }), [orders]);

    const filtered = useMemo(() => {
        if (tab === "overrides") return orders.filter((o) => o.order_status === "Override Pending");
        if (tab === "active") return orders.filter((o) => !["Delivered", "Cancelled"].includes(o.order_status));
        return orders;
    }, [orders, tab]);

    const submitNew = async (e) => {
        e.preventDefault();
        setModalErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/orders", { ...form, quantity: Number(form.quantity) });
            if (data.order_status === "Override Pending") {
                toast.warning(`${data.order_id} routed to Override Queue: ${data.block_reason || "Mission Ready lock"}`);
            } else {
                toast.success(`${data.order_id} created`);
            }
            setCreating(false);
            setForm({ customer_name: "", sku: "", quantity: 1, fulfillment_type: "Internal" });
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };

    const advanceState = async (o) => {
        const next = NEXT_STATE[o.order_status];
        if (!next) return;
        try {
            await api.patch(`/orders/${o.id}`, { order_status: next });
            toast.success(`${o.order_id} → ${next}`);
            refresh();
        } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message); }
    };

    const decideOverride = async () => {
        if (!overrideTarget || overrideReason.trim().length < 3) return;
        setBusy(true);
        try {
            const path = overrideTarget.action === "approve" ? "approve-override" : "reject-override";
            await api.post(`/orders/${overrideTarget.order.id}/${path}`, { reason: overrideReason });
            toast.success(`${overrideTarget.order.order_id} override ${overrideTarget.action}d`);
            setOverrideTarget(null);
            setOverrideReason("");
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };

    if (loading) return <div className="text-snow-dim font-mono text-sm">Loading orders…</div>;

    return (
        <div className="space-y-6" data-testid="orders-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="overline">Demand / Sales</div>
                    <h1 className="heading-display text-4xl mt-1 flex items-center gap-3">
                        <ShoppingCart className="w-8 h-8 text-signal" /> Orders Terminal
                    </h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Customer orders, state transitions, and the Mission-Ready hard-lock override queue.
                    </p>
                </div>
                {perm.manageOrders && (
                    <button
                        data-testid="create-order-btn"
                        onClick={() => setCreating(true)}
                        className="bg-signal text-jet hover:bg-signal-hover px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2"
                    >
                        <Plus className="w-3 h-3" /> New Order
                    </button>
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Stat label="Total" value={stats.total} testid="orders-stat-total" />
                <Stat label="Pending" value={stats.pending} testid="orders-stat-pending" />
                <Stat label="Allocated" value={stats.allocated} testid="orders-stat-allocated" />
                <Stat label="Shipped" value={stats.shipped} testid="orders-stat-shipped" />
                <Stat label="Override Queue" value={stats.overrides} alert={stats.overrides > 0} testid="orders-stat-overrides" />
            </div>

            <div className="flex border border-[#2A2A2A] w-fit" data-testid="orders-tab-bar">
                {[
                    { id: "active", label: "Active" },
                    perm.approveOrderOverride && { id: "overrides", label: `Overrides (${stats.overrides})` },
                    { id: "all", label: "All" },
                ].filter(Boolean).map((t) => (
                    <button
                        key={t.id}
                        data-testid={`orders-tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                            tab === t.id ? "bg-signal text-jet" : "text-snow-dim hover:text-snow"
                        }`}
                    >{t.label}</button>
                ))}
            </div>

            <div className="border border-[#2A2A2A] bg-jet-elev1 overflow-x-auto">
                <table className="w-full" data-testid="orders-table">
                    <thead><tr className="border-b border-[#2A2A2A]">
                        <Th>Order</Th><Th>Customer</Th><Th>SKU</Th><Th>Qty</Th><Th>Type</Th><Th>Status</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={7} className="px-5 py-8 text-center text-snow-muted text-xs font-mono">No orders match this filter.</td></tr>
                        ) : filtered.map((o) => (
                            <tr key={o.id} data-testid={`order-row-${o.order_id}`} className="border-b border-[#2A2A2A] hover:bg-white/[0.03]">
                                <Td><span className="font-mono text-[12px] text-signal">{o.order_id}</span></Td>
                                <Td><span className="text-sm">{o.customer_name}</span></Td>
                                <Td><span className="font-mono text-xs text-snow-dim">{o.sku}</span></Td>
                                <Td><span className="font-mono tabular-nums">{o.quantity}</span></Td>
                                <Td><span className="text-[11px] font-mono">{o.fulfillment_type}</span></Td>
                                <Td>
                                    <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${STATUS_CHIP[o.order_status] || "border-[#2A2A2A]"}`}>
                                        {o.order_status}
                                    </span>
                                    {o.block_reason && (
                                        <div className="text-[10px] text-signal mt-1 italic" data-testid={`order-block-reason-${o.order_id}`}>
                                            <AlertTriangle className="inline w-3 h-3 mr-1" />{o.block_reason}
                                        </div>
                                    )}
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {/* Advance state — gated to transition_order */}
                                        {perm.transitionOrder && NEXT_STATE[o.order_status] && (
                                            <button
                                                data-testid={`advance-${o.order_id}`}
                                                onClick={() => advanceState(o)}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-signal text-signal hover:bg-signal hover:text-jet transition-colors flex items-center gap-1"
                                            >
                                                <ArrowRight className="w-3 h-3" /> {NEXT_STATE[o.order_status]}
                                            </button>
                                        )}
                                        {/* Override decision — gated to approve_order_override */}
                                        {perm.approveOrderOverride && o.order_status === "Override Pending" && (
                                            <>
                                                <button
                                                    data-testid={`approve-override-${o.order_id}`}
                                                    onClick={() => { setOverrideTarget({ order: o, action: "approve" }); setOverrideReason(""); }}
                                                    className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-green-500/60 text-green-400 hover:bg-green-500/10 transition-colors flex items-center gap-1"
                                                ><CheckCircle2 className="w-3 h-3" /> Approve</button>
                                                <button
                                                    data-testid={`reject-override-${o.order_id}`}
                                                    onClick={() => { setOverrideTarget({ order: o, action: "reject" }); setOverrideReason(""); }}
                                                    className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-red-500/60 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1"
                                                ><XCircle className="w-3 h-3" /> Reject</button>
                                            </>
                                        )}
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <Modal title="New Order" onClose={() => setCreating(false)}>
                    <form onSubmit={submitNew} className="space-y-4" data-testid="order-form">
                        <Field label="Customer Name">
                            <input data-testid="order-customer-input" required minLength={2} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="cerb-input" />
                        </Field>
                        <Field label="SKU">
                            <input data-testid="order-sku-input" required minLength={2} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} className="cerb-input" placeholder="VLT-SVC-CRT" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Quantity">
                                <input data-testid="order-qty-input" type="number" min={1} required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="cerb-input" />
                            </Field>
                            <Field label="Fulfillment">
                                <select data-testid="order-fulfillment-select" value={form.fulfillment_type} onChange={(e) => setForm({ ...form, fulfillment_type: e.target.value })} className="cerb-input">
                                    <option>Internal</option>
                                    <option>Drop Ship</option>
                                </select>
                            </Field>
                        </div>
                        {modalErr && <div className="text-red-400 text-xs font-mono">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={() => setCreating(false)} className="cerb-btn-secondary">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="order-submit-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Order"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {overrideTarget && (
                <Modal title={`${overrideTarget.action === "approve" ? "Approve" : "Reject"} Override — ${overrideTarget.order.order_id}`} onClose={() => setOverrideTarget(null)} accent>
                    <div className="space-y-4" data-testid="override-modal">
                        <div className="border border-signal bg-signal/5 px-3 py-2 text-xs font-mono text-signal flex items-start gap-2">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span>{overrideTarget.order.block_reason || "Mission Ready lock active."}</span>
                        </div>
                        <Field label="Reason (audit-logged)">
                            <textarea data-testid="override-reason-input" required minLength={3} maxLength={500} rows={3} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="cerb-input" />
                        </Field>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setOverrideTarget(null)} className="cerb-btn-secondary">Cancel</button>
                            <button
                                onClick={decideOverride}
                                disabled={busy || overrideReason.trim().length < 3}
                                className="cerb-btn-primary"
                                data-testid="override-confirm-btn"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${overrideTarget.action === "approve" ? "Approve" : "Reject"} Override`}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ===== shared atoms =====
function Stat({ label, value, alert, testid }) {
    return (
        <div data-testid={testid} className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-4`}>
            <div className="overline">{label}</div>
            <div className={`heading-display text-3xl mt-2 tabular-nums ${alert ? "text-signal" : "text-snow"}`}>{value}</div>
        </div>
    );
}
function Th({ children }) { return <th className="text-left px-5 py-3 overline font-normal">{children}</th>; }
function Td({ children }) { return <td className="px-5 py-3 align-middle">{children}</td>; }
function Field({ label, children }) { return <div><label className="overline block mb-2">{label}</label>{children}</div>; }
function Modal({ title, children, onClose, accent }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
